import { PaginatedParticipants, Participant } from 'typings/participant';
import { api, PaginatedBackendResponse } from './api';
import { ParticipantActivityWithoutReportUrl } from '../../typings/participant_activity';

export interface FeedbackList {
    [userId: string]: {
        experienceDescription: string;
        improveSuggestion: string;
        feedbackRate: number;
    };
}

export type EmailTemplateType =
    | 'UserInvitation'
    | 'UserReminder'
    | 'UserReport'
    | 'UserSetPassword'
    | 'ScanCompletion'
    | 'RecruiterScanCompletion'
    | 'RecruiterReportGeneration'
    | 'RecruiterInvitationNewAccount'
    | 'RecruiterInvitationExistingAccount'
    | 'UserScanReset';

interface SendEmailsResponse {
    success: string[];
    failed: {
        [userId: string]:
            | 'UserNotFound'
            | 'InvitationNotFound'
            | 'InvitationNotActive'
            | 'AlreadySent'
            | 'BaseGroup'
            | 'ReportNotFound'
            | 'FailedToSend';
    };
}

export interface EmailAPI {
    id: string;
    target_group_id?: string;
    company_id?: string;
    user_id: string;
    template: EmailTemplateType;
    sent_at: string;
    delivered_at?: string;
    delivered: boolean;
    bounced: boolean;
}

export interface AddParticipantInput {
    email: string;
    first_name: string;
    last_name: string;
}

interface AddParticipantResponse {
    user_id: string;
    user_email: string;
    invitation_id: string;
    first_name: string;
    last_name: string;
}

export interface BatchProcessParticipantResponse {
    success: string[];
    error: Record<
        string,
        | 'UserNotFound'
        | 'InvitationNotFound'
        | 'FailedToDeleteAnalysisReference'
        | 'UnexpectedError'
        | 'FailedToAnonymizeUserFiles'
        | 'FailedToAnonymizeUser'
        | 'AnalysisReferenceNotFound'
    >;
    warning: Record<
        string,
        | 'CandidateHasOtherRoles'
        | 'CandidateHasOtherInvitations'
        | 'InvitationNotFound'
        | 'UserHasOtherInvitations'
        | 'UserHasOtherRoles'
    >;
}

type SendEmailTypes = 'invitation' | 'reminder' | 'report';

export const participants = api.injectEndpoints({
    endpoints: (builder) => ({
        addParticipant: builder.mutation<
            AddParticipantResponse[],
            {
                companyIdentifier: string;
                groupId: string;
                participantsToAdd: AddParticipantInput[];
            }
        >({
            query: ({ companyIdentifier, groupId, participantsToAdd }) => ({
                url: `companies/${companyIdentifier}/groups/${groupId}/participants`,
                method: 'POST',
                body: {
                    participants: participantsToAdd,
                },
            }),
            invalidatesTags: (result, error, { groupId }) => [
                {
                    type: 'Participant',
                    groupId: groupId,
                },
            ],
        }),

        getParticipants: builder.query<
            PaginatedParticipants,
            {
                companyIdentifier: string;
                groupId: string;
                limit: number;
                includeTotal: boolean;
                includeResults: boolean;
                page?: number;
                nextToken?: string;
            }
        >({
            query: ({
                companyIdentifier,
                groupId,
                limit,
                includeTotal,
                includeResults,
                page,
                nextToken,
            }) => {
                const params: {
                    limit: number;
                    include_results: boolean;
                    include_total?: boolean;
                    page?: number;
                    next_token?: string;
                } = {
                    limit,
                    include_results: includeResults,
                    include_total: includeTotal,
                };

                if (page !== undefined) {
                    params.page = page;
                }

                if (nextToken !== undefined) {
                    params.next_token = nextToken;
                }

                return {
                    url: `companies/${companyIdentifier}/groups/${groupId}/participants`,
                    params,
                };
            },
            transformResponse: (
                response: PaginatedBackendResponse<Participant>,
            ) => ({
                participants: response.items,
                count: response.count,
                total: response.total ?? undefined,
                nextToken: response.nextToken ?? undefined,
                currentPage: response.currentPage ?? undefined,
                totalPages: response.totalPages ?? undefined,
            }),
            providesTags: (result, error, { groupId, page }) => [
                {
                    type: 'Participant',
                    id: `${groupId}:${page}`,
                    groupId,
                },
            ],
        }),

        getParticipantActivity: builder.query<
            ParticipantActivityWithoutReportUrl,
            {
                companyIdentifier: string;
                groupId: string;
                userId: string;
                includeReportUrl: boolean;
            }
        >({
            query: ({
                companyIdentifier,
                groupId,
                userId,
                includeReportUrl,
            }) => ({
                url: `companies/${companyIdentifier}/groups/${groupId}/users/${userId}/activity`,
                params: {
                    include_report_url: includeReportUrl,
                },
            }),
            providesTags: (
                result,
                error,
                { companyIdentifier, groupId, userId },
            ) => [
                {
                    type: 'ParticipantActivity',
                    id: `${companyIdentifier}:${groupId}:${userId}`,
                },
            ],
        }),

        getParticipantsActivity: builder.query<
            ParticipantActivityWithoutReportUrl[],
            {
                companyIdentifier: string;
                groupId: string;
                userIds: string[];
            }
        >({
            query: ({ companyIdentifier, groupId, userIds }) => ({
                url: `companies/${companyIdentifier}/groups/${groupId}/activity`,
                method: 'POST',
                body: {
                    user_ids: userIds,
                },
            }),
            providesTags: (
                result,
                error,
                { companyIdentifier, groupId, userIds },
            ) => {
                const userTags = userIds.map(
                    (userId) => `${companyIdentifier}:${groupId}:${userId}`,
                );
                return [
                    {
                        type: 'ParticipantsActivity',
                        id: `${companyIdentifier}:${groupId}`,
                    },
                    ...userTags.map((userTag) => ({
                        type: 'ParticipantsActivity' as const,
                        userId: userTag,
                    })),
                ];
            },
        }),

        getFeedback: builder.query<
            FeedbackList,
            {
                companyIdentifier: string;
                groupId: string;
                userIds: string[];
            }
        >({
            query: ({ companyIdentifier, groupId, userIds }) => ({
                url: `companies/${companyIdentifier}/groups/${groupId}/feedback`,
                method: 'POST',
                body: {
                    user_ids: userIds,
                },
            }),
            providesTags: (
                result,
                error,
                { companyIdentifier, groupId, userIds },
            ) => [
                {
                    type: 'ParticipantFeedback',
                    id: `${companyIdentifier}:${groupId}:${userIds.join('|')}`,
                },
            ],
        }),

        getParticipantsEmailsInfo: builder.query<
            EmailAPI[],
            {
                companyIdentifier: string;
                groupId: string;
                userId: string;
            }
        >({
            query: ({ companyIdentifier, groupId, userId }) =>
                `companies/${companyIdentifier}/groups/${groupId}/emails/${userId}`,
            providesTags: (
                result,
                error,
                { companyIdentifier, groupId, userId },
            ) => [
                {
                    type: 'ParticipantEmailInfo',
                    id: `${companyIdentifier}:${groupId}:${userId}`,
                },
            ],
        }),

        batchProcessParticipant: builder.mutation<
            BatchProcessParticipantResponse,
            {
                companyIdentifier: string;
                groupId: string;
                operation: 'delete' | 'full_delete' | 'reset' | 'anonymize';
                userIds: string[];
            }
        >({
            query: ({ companyIdentifier, groupId, operation, userIds }) => ({
                url: `companies/${companyIdentifier}/groups/${groupId}/participants`,
                method: 'PATCH',
                body: {
                    operation,
                    user_ids: userIds,
                },
            }),
            invalidatesTags: (
                result,
                error,
                { companyIdentifier, groupId, operation, userIds },
            ) => {
                if (operation !== 'anonymize') {
                    return [
                        {
                            type: 'Participant',
                            groupId,
                        },
                    ];
                }
                if (operation === 'anonymize') {
                    return userIds.map((userId) => ({
                        type: 'ParticipantActivity',
                        id: `${companyIdentifier}:${groupId}:${userId}`,
                    }));
                }
                return [];
            },
        }),

        sendParticipantEmails: builder.mutation<
            SendEmailsResponse,
            {
                companyIdentifier: string;
                groupId: string;
                emailType: SendEmailTypes;
                userIds: string[];
                resend: boolean;
            }
        >({
            query: ({
                companyIdentifier,
                groupId,
                emailType,
                userIds,
                resend,
            }) => ({
                url: `companies/${companyIdentifier}/groups/${groupId}/emails`,
                method: 'POST',
                body: {
                    email: emailType,
                    user_ids: userIds,
                    resend: resend,
                },
            }),
            invalidatesTags: (result, error, { groupId }) => [
                {
                    type: 'Participant',
                    groupId: groupId,
                },
            ],
        }),

        sendResetPasswordEmails: builder.mutation<
            SendEmailsResponse,
            {
                groupId: string;
                userIds: string[];
            }
        >({
            query: ({ userIds }) => ({
                url: `/emails/reset-password`,
                method: 'POST',
                body: {
                    client: 'dashboard',
                    user_ids: userIds,
                },
            }),
        }),
    }),
});

export const {
    useAddParticipantMutation,
    useGetParticipantsQuery,
    useLazyGetParticipantsQuery,
    useGetParticipantActivityQuery,
    useLazyGetParticipantActivityQuery,
    useGetParticipantsActivityQuery,
    useLazyGetParticipantsActivityQuery,
    useGetFeedbackQuery,
    useLazyGetFeedbackQuery,
    useGetParticipantsEmailsInfoQuery,
    useLazyGetParticipantsEmailsInfoQuery,
    useBatchProcessParticipantMutation,
    useSendParticipantEmailsMutation,
    useSendResetPasswordEmailsMutation,
    util: { getRunningQueriesThunk },
} = participants;

// Export the API slice for use in SSR (if needed)
export const { getParticipants, getParticipantActivity } =
    participants.endpoints;
